import type { ReactNode } from "react";
import { Link } from "gatsby";

// Coordonnées du marqueur de la fiche Google Maps, confirmées par géocodage
// inverse OpenStreetMap sur le chemin du Sec Iton.
const latitude = 48.9475864;
const longitude = 1.0448116;

export const contact = {
  phone: "06 34 71 99 11",
  phoneHref: "tel:+33634719911",
  email: "contact@lesecuriesducerf.fr",
  emailHref: "mailto:contact@lesecuriesducerf.fr",
  street: "1 chemin du Sec Iton",
  city: "27190 Gaudreville-la-Rivière",
  area: "Gaudreville-la-Rivière — entre Évreux et Conches-en-Ouche (27)",
  areaShort: "Gaudreville-la-Rivière (27)",
  latitude,
  longitude,
  mapUrl: "https://goo.gl/maps/z3cCqJ5e86964i659",
};

export type Itineraire = {
  label: string;
  href: string;
};

// Google Maps et Waze référencent l'établissement : on leur passe son nom, ce
// qui affiche la fiche des écuries au lieu d'un point anonyme. Les autres
// services n'ont pas de fiche garantie et reçoivent les coordonnées — en
// campagne, un numéro dans un chemin communal n'est pas résolu de la même façon
// d'un service à l'autre, alors qu'un couple lat/lon désigne toujours l'entrée.
const destination = `${latitude},${longitude}`;
const etablissement = encodeURIComponent(
  `Les écuries du Cerf, 27190 Gaudreville-la-Rivière`,
);

export const itineraires: Itineraire[] = [
  {
    label: "Google Maps",
    href: `https://www.google.com/maps/dir/?api=1&destination=${etablissement}`,
  },
  {
    label: "Waze",
    href: `https://waze.com/ul?q=${etablissement}&navigate=yes`,
  },
  {
    label: "Plans",
    href: `https://maps.apple.com/?daddr=${destination}&dirflg=d`,
  },
  {
    label: "OpenStreetMap",
    // `route=;<destination>` laisse le point de départ vide : le visiteur saisit
    // le sien sur la page. Le moteur est fixé sur la voiture.
    href: `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=%3B${latitude}%2C${longitude}`,
  },
];

export const navPages = [
  { label: "Présentation", href: "/" },
  { label: "Pensions", href: "/pension/" },
  { label: "Infrastructures", href: "/infrastructures/" },
  { label: "Commerce", href: "/commerce/" },
  { label: "Transport", href: "/transport/" },
  { label: "Gîte", href: "/gite/" },
];

const infraLink = <Link to="/infrastructures/">infrastructures</Link>;

export type Pension = {
  name: string;
  price: string;
  unit: string;
  summary: string;
  items: ReactNode[];
  dark?: boolean;
};

export const pensions: Pension[] = [
  {
    name: "Classique",
    price: "360€",
    unit: "/mois",
    summary: "Le quotidien complet d'un cheval au box, sorties incluses",
    items: [
      "Box paille",
      "2 à 3 repas de granulés par jour (selon le besoin du cheval)",
      "2 repas foin par jour",
      <>accès aux {infraLink}</>,
      "Pansage quotidien de votre équidé",
      "Sortie quotidienne au paddock",
    ],
  },
  {
    name: "Grand paddock",
    price: "150€",
    unit: "/mois",
    summary: "La vie au grand air, au paddock toute l'année",
    items: [
      "2 repas foin par jour",
      <>accès aux {infraLink}</>,
      "Pansage quotidien de votre équidé",
    ],
  },
  {
    name: "Convalescence",
    price: "600€",
    unit: "/mois",
    summary: "Soins quotidiens et sorties adaptées au rétablissement",
    items: [
      "Box paille / ou copeaux +100€",
      "Foin ou enrhubanné + ration en fonction du besoin",
      "Soins quotidiens (IV, IM, gouttes, argile, ...) selon avis vétérinaire",
      "Sortie quotidienne en fonction de la convalescence (pas en main, marcheur, longe, paddock)",
    ],
  },
  {
    name: "Horse-sitting",
    price: "20€",
    unit: "/nuit",
    summary: "Le même quotidien, à la nuit, pendant vos absences",
    dark: true,
    items: [
      "Box paille ou copeaux",
      "2 à 3 repas granulés par jour",
      "2 repas foin par jour",
      "Sortie au paddock ou au marcheur",
      "Sortie en longe ou monté",
      "Pansage quotidien de votre équidé",
    ],
  },
];

export type Infra = {
  file: string;
  title: string;
  sub: string;
  alt: string;
  home: boolean;
};

export const infrastructures: Infra[] = [
  {
    file: "carriere.jpg",
    title: "Carrière 50×40 m",
    sub: "Éclairée et encadrée de lisses en bois",
    alt: "La carrière des écuries du cerf",
    home: true,
  },
  {
    file: "manege.jpg",
    title: "Manège 30×15 m",
    sub: "Éclairé — travail par tous les temps",
    alt: "Le manège des écuries du cerf",
    home: true,
  },
  {
    file: "ronddelonge.jpeg",
    title: "Rond de longe",
    sub: "Travail en longe en toute sécurité",
    alt: "Le rond de longe des écuries du cerf",
    home: true,
  },
  {
    file: "rondDeMarche.jpg",
    title: "Marcheur 4 places",
    sub: "Sorties quotidiennes encadrées",
    alt: "Le rond de marche des écuries du cerf",
    home: true,
  },
  {
    file: "box.jpg",
    title: "Box",
    sub: "Paille ou copeaux",
    alt: "Les box des écuries du cerf",
    home: false,
  },
  {
    file: "salle-de-soin.jpg",
    title: "2 salles de soins",
    sub: "Éclairées",
    alt: "Une salle de soins des écuries du cerf",
    home: false,
  },
  {
    file: "solarium.jpg",
    title: "2 solariums",
    sub: "Séchage et récupération",
    alt: "Un solarium des écuries du cerf",
    home: true,
  },
];

export const requiredPhotos = [
  ...infrastructures.map((infra) => infra.file),
  "paddock.jpg",
  "gite.jpg",
  "ecurie_victoria_goupillot_commerce_chevaux_poney.jpg",
];

export const giteEquipements = [
  "Wifi",
  "Télévision",
  "Lave-linge",
  "Lave-vaisselle",
  "Cafetière à filtre",
  "Cafetière Tassimo",
  "Grille-pain",
  "Bouilloire",
  "Aspirateur",
  "Balai nettoyant",
  "Sèche-cheveux",
];
